// /admin/teams — team-centric permission management. Primary admin
// surface under the team + permissions model.
//
// Reads:
//   root.teams              — all teams across orgs the user belongs to
//                             (loaded by loadAllTeams). Filtered to the
//                             active workspace org in the UI.
//   root.teamsByEmail       — reverse index for per-team member counts
//   root.jwtClaims.workspace_org_id — active org (scoping filter)
//   root.jwtClaims.is_owner / org_role / permissions — gating UI actions
//
// Routes:
//   /admin/teams            → list (grouped by category)
//   /admin/teams?id=<id>    → detail (matrix + members + invite)
//
// Writes (SDK wrappers in functions/teams.js):
//   createTeam, addMemberToTeam, removeMemberFromTeam, updateTeamPermissions
//   inviteToTeam (new) — POST /organizations/:orgId/teams/:teamId/invitations

import {
  PERMISSION_GROUPS,
  PERMISSIONS_BY_GROUP,
  permLabel,
  permDescription,
  hasPerm,
  isOwner
} from '../../../workspace-project/functions/permissions.js'
import { readGovernanceClaims } from '../../functions/diagnoseAuth.js'

const _activeOrgId = (root) => root?.jwtClaims?.workspace_org_id || null

const _teamsInActiveOrg = (root) =>
  (root?.teams || []).filter((t) => t.orgId === _activeOrgId(root))

// Selected team id from URL search. Reads `root.currentSearch` (kept in
// sync by installAppShellSync on popstate/locationchange) so `hide` +
// children factories react when the user clicks a team row.
const _selectedTeamId = (root) => {
  const search = root?.currentSearch ?? (typeof window !== 'undefined' ? window.location.search : '')
  try { return new URLSearchParams(search).get('id') || null }
  catch { return null }
}

const _findSelectedTeam = (root) => {
  const id = _selectedTeamId(root)
  return id ? (root?.teams || []).find((t) => t.id === id) || null : null
}

// Members of a specific team, derived from teamsByEmail. Returns array
// of { email, role, userId } for use in both count (length) and list
// render — one pass, two consumers.
const _teamMembers = (teamId, teamsByEmail) => {
  const rows = []
  for (const email in teamsByEmail || {}) {
    const entry = (teamsByEmail[email] || []).find((t) => t.id === teamId)
    if (entry) rows.push({ email, role: entry.role, userId: entry.userId })
  }
  return rows
}

// ── Row in the team list ────────────────────────────────────────────

const _teamRow = {
  display: 'grid',
  gridTemplateColumns: '2fr auto auto',
  gap: 'Z',
  align: 'center',
  padding: 'Z1 A',
  borderBottomWidth: '1px',
  borderBottomStyle: 'solid',
  borderBottomColor: 'line',
  cursor: 'pointer',
  ':hover': { background: 'overlayStrong' },

  Left: {
    flow: 'y',
    gap: 'V',
    minWidth: '0',
    Name: {
      tag: 'span',
      fontSize: 'A',
      fontWeight: '600',
      color: 'title',
      text: (el, s) => s.name || s.slug
    },
    Meta: {
      tag: 'span',
      fontSize: 'Z2',
      color: 'caption',
      text: (el, s) => {
        const cat = s.category || 'active'
        const sys = s.isSystem ? ' • system' : ''
        const permCount = (s.permissions || []).length
        const perms = permCount ? ` • ${permCount} perm${permCount === 1 ? '' : 's'}` : ''
        return `${cat}${sys}${perms}`
      }
    }
  },

  Count: {
    tag: 'span',
    padding: 'V Z',
    round: 'Y',
    fontSize: 'Y1',
    fontWeight: '700',
    color: 'caption',
    background: 'overlayStrong',
    text: (el, s) => {
      // Members are lazy-loaded on detail view open. Before a team has
      // been opened, teamsByEmail contains no entries for it — show a
      // neutral placeholder rather than misleading "0 members".
      const loaded = s.root?.teamMembersLoaded?.[s.id]
      if (!loaded) return '— members'
      const n = _teamMembers(s.id, s.root?.teamsByEmail).length
      return `${n} ${n === 1 ? 'member' : 'members'}`
    }
  },

  Chevron: { tag: 'span', fontSize: 'Z1', color: 'caption', text: '›' },

  onClick: (e, el, s) => {
    const root = el.call('findRouteRoot', el)
    if (root) {
      s.root.update({ _teamInviteEmail: '', _teamInviteStatus: null })
      el.router(`/admin/teams?id=${encodeURIComponent(s.id)}`, root)
    }
  }
}

// ── Permission checkbox row (writable) ──────────────────────────────

const _permRow = {
  display: 'grid',
  gridTemplateColumns: 'auto 1fr',
  gap: 'Z',
  padding: 'V Z',
  align: 'start',
  cursor: 'pointer',
  ':hover': { background: 'overlay' },

  Box: {
    tag: 'input',
    type: 'checkbox',
    marginTop: 'V',
    checked: (el, s) => {
      const team = _findSelectedTeam(s.root)
      return !!team && (team.permissions || []).includes(s.key)
    },
    disabled: (el, s) => !hasPerm(s.root?.jwtClaims, 'admin.team_permissions'),
    onChange: async (e, el, s) => {
      const team = _findSelectedTeam(s.root)
      if (!team) return
      const current = new Set(team.permissions || [])
      if (e.target.checked) current.add(s.key)
      else current.delete(s.key)
      try {
        await el.call('updateTeamPermissions', s.root, {
          orgId: team.orgId,
          teamId: team.id,
          permissions: [...current]
        })
      } catch (err) {
        console.warn('[admin/teams] permission toggle failed', err)
        e.target.checked = !e.target.checked
      }
    }
  },

  Detail: {
    flow: 'y',
    gap: 'V',
    Label: {
      tag: 'span',
      fontSize: 'Z2',
      fontWeight: '600',
      color: 'title',
      text: (el, s) => permLabel(s.key)
    },
    Description: {
      tag: 'span',
      fontSize: 'Y1',
      color: 'caption',
      text: (el, s) => permDescription(s.key)
    }
  }
}

// ── Member row (detail pane) ────────────────────────────────────────

const _memberRow = {
  display: 'grid',
  gridTemplateColumns: '1fr auto auto',
  gap: 'Z',
  align: 'center',
  padding: 'V Z',
  borderBottomWidth: '1px',
  borderBottomStyle: 'solid',
  borderBottomColor: 'line',

  Info: {
    flow: 'y',
    gap: 'V',
    Email: {
      tag: 'span',
      fontSize: 'Z2',
      fontWeight: '600',
      color: 'title',
      text: (el, s) => s.email || '—'
    },
    Role: {
      tag: 'span',
      fontSize: 'Y1',
      color: 'caption',
      text: (el, s) => s.role || 'member'
    }
  },
  Reinvite: {
    tag: 'button',
    text: 'Reinvite',
    title: 'Send a fresh invitation email so the user can activate / sign in',
    padding: 'V Z',
    round: 'Y',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'line',
    background: 'transparent',
    color: 'title',
    cursor: 'pointer',
    fontSize: 'Y1',
    ':hover': { background: 'overlay' },
    onClick: async (e, el, s) => {
      const team = _findSelectedTeam(s.root)
      if (!team || !s.email) return
      s.root.update({ _teamInviteStatus: { tone: 'pending', message: `Re-inviting ${s.email}…` } })
      try {
        await el.call('inviteToTeam', s.root, {
          orgId: team.orgId,
          teamId: team.id,
          email: s.email
        })
        s.root.update({ _teamInviteStatus: { tone: 'success', message: `Invitation re-sent to ${s.email}.` } })
      } catch (err) {
        console.warn('[admin/teams] reinvite failed', err)
        s.root.update({ _teamInviteStatus: { tone: 'error', message: `Reinvite failed: ${err?.message || err}` } })
      }
    }
  },
  Remove: {
    tag: 'button',
    text: 'Remove',
    padding: 'V Z',
    round: 'Y',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'line',
    background: 'transparent',
    color: 'title',
    cursor: 'pointer',
    fontSize: 'Y1',
    ':hover': { background: 'overlay' },
    onClick: async (e, el, s) => {
      const team = _findSelectedTeam(s.root)
      if (!team) return
      if (!confirm(`Remove ${s.email} from ${team.name}?`)) return
      try {
        await el.call('removeMemberFromTeam', s.root, {
          orgId: team.orgId,
          teamId: team.id,
          userId: s.userId,
          email: s.email
        })
      } catch (err) {
        console.warn('[admin/teams] remove member failed', err)
      }
    }
  }
}

// ── Page ────────────────────────────────────────────────────────────

export const adminTeams = {
  extends: 'AppPage',
  metadata: { title: 'Teams / Admin / Symbols Workspace' },
  onInit: (el, s) => {
    // Fall back to readGovernanceClaims() so a fresh page load doesn't
    // race subscribeAuthSession and falsely redirect owners.
    const claims = s.root?.jwtClaims || readGovernanceClaims()
    if (isOwner(claims) || claims?.org_role === 'admin') return
    if (!el.call('shouldRedirectNoPerm', s, 'admin.pages.read')) return
    const root = el.call('findRouteRoot', el)
    if (root) el.router('/', root, {}, { pushState: true })
  },

  AdminSidebar: {},

  TeamsMain: {
    extends: 'AppMain',
    padding: '0',

    Header: {
      extends: 'PageHeader',
      LeftGroup: { Title: { text: 'Teams & Permissions' } },
      RightGroup: {
        BackBtn: {
          tag: 'button',
          text: '← All teams',
          hide: (el, s) => !_selectedTeamId(s.root),
          padding: 'V Z1',
          round: 'Y',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'line',
          background: 'transparent',
          color: 'title',
          cursor: 'pointer',
          fontSize: 'Z2',
          ':hover': { background: 'overlay' },
          onClick: (e, el, s) => {
            const root = el.call('findRouteRoot', el)
            if (root) {
              s.root.update({ _teamInviteEmail: '', _teamInviteStatus: null })
              el.router('/admin/teams', root)
            }
          }
        },
        RefreshBtn: {
          tag: 'button',
          text: '↻ Refresh perms',
          padding: 'V Z1',
          round: 'Y',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'line',
          background: 'transparent',
          color: 'title',
          cursor: 'pointer',
          fontSize: 'Z2',
          ':hover': { background: 'overlay' },
          onClick: async (e, el, s) => {
            try {
              const mod = await import('../../governanceBridge.js')
              await mod.refreshClaims?.()
              window.location.reload()
            } catch (err) {
              console.warn('[admin/teams] refreshClaims failed', err)
            }
          }
        }
      }
    },

    Body: {
      overflow: 'auto',
      padding: 'B',
      flow: 'y',
      gap: 'A',

      // ── LIST VIEW ────────────────────────────────────────────────
      ListView: {
        hide: (el, s) => !!_selectedTeamId(s.root),
        flow: 'y',
        gap: 'A',

        CreateTeamBar: {
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          gap: 'Z1',
          align: 'center',
          padding: 'Z1 A',
          round: 'Z',
          background: 'overlay',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'line',
          Hint: {
            tag: 'span',
            fontSize: 'Z2',
            color: 'caption',
            text: 'Create a new team for this org. System teams are seeded automatically.'
          },
          CreateBtn: {
            tag: 'button',
            text: '+ Create team',
            padding: 'V Z1',
            round: 'Y',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: 'line',
            background: 'brand',
            color: 'onBrand',
            cursor: 'pointer',
            fontSize: 'Z2',
            onClick: async (e, el, s) => {
              const orgId = _activeOrgId(s.root)
              if (!orgId) return
              const name = prompt('Team name')
              if (!name) return
              try {
                await el.call('createTeam', s.root, { orgId, name })
              } catch (err) {
                console.warn('[admin/teams] createTeam failed', err)
              }
            }
          }
        },

        ActiveSection: {
          flow: 'y',
          gap: 'Z1',
          Heading: {
            tag: 'h2',
            fontSize: 'A1',
            fontWeight: '800',
            color: 'title',
            margin: '0',
            text: 'Active teams'
          },
          List: {
            round: 'Z2',
            background: 'overlay',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: 'line',
            overflow: 'hidden',
            childExtends: _teamRow,
            childrenAs: 'state',
            children: (el, s) =>
              _teamsInActiveOrg(s.root).filter((t) => (t.category || 'active') === 'active')
          },
          Empty: {
            tag: 'span',
            hide: (el, s) =>
              _teamsInActiveOrg(s.root).filter((t) => (t.category || 'active') === 'active').length > 0,
            padding: 'Z1 A',
            fontSize: 'Z2',
            color: 'caption',
            text: 'No active teams yet.'
          }
        },

        ExternalSection: {
          flow: 'y',
          gap: 'Z1',
          Heading: {
            tag: 'h2',
            fontSize: 'A1',
            fontWeight: '800',
            color: 'title',
            margin: '0',
            text: 'External'
          },
          List: {
            round: 'Z2',
            background: 'overlay',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: 'line',
            overflow: 'hidden',
            childExtends: _teamRow,
            childrenAs: 'state',
            children: (el, s) => _teamsInActiveOrg(s.root).filter((t) => t.category === 'external')
          },
          Empty: {
            tag: 'span',
            hide: (el, s) =>
              _teamsInActiveOrg(s.root).filter((t) => t.category === 'external').length > 0,
            padding: 'Z1 A',
            fontSize: 'Z2',
            color: 'caption',
            text: 'No external teams yet.'
          }
        }
      },

      // ── DETAIL VIEW ──────────────────────────────────────────────
      DetailView: {
        hide: (el, s) => !_selectedTeamId(s.root),
        flow: 'y',
        gap: 'A',

        TeamHeader: {
          flow: 'y',
          gap: 'V',
          Name: {
            tag: 'h2',
            fontSize: 'B1',
            fontWeight: '800',
            color: 'title',
            margin: '0',
            text: (el, s) => _findSelectedTeam(s.root)?.name || 'Team'
          },
          Meta: {
            tag: 'span',
            fontSize: 'Z2',
            color: 'caption',
            text: (el, s) => {
              const t = _findSelectedTeam(s.root)
              if (!t) return ''
              return `${t.category || 'active'}${t.isSystem ? ' • system' : ''} • slug: ${t.slug}`
            }
          }
        },

        // Permissions matrix
        PermsCard: {
          flow: 'y',
          gap: 'Z1',
          padding: 'A',
          round: 'Z2',
          background: 'overlay',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'line',

          Header: {
            tag: 'h3',
            fontSize: 'A1',
            fontWeight: '700',
            color: 'title',
            margin: '0 0 V 0',
            text: 'Permissions'
          },
          HintRow: {
            tag: 'span',
            fontSize: 'Y1',
            color: 'caption',
            margin: '0 0 Z 0',
            text: (el, s) => {
              const claims = s.root?.jwtClaims
              if (isOwner(claims)) return 'Owners bypass all permission checks. Toggles below affect non-owner members only.'
              if (hasPerm(claims, 'admin.team_permissions')) return 'Changes save on toggle.'
              return 'Read-only — requires admin.team_permissions.'
            }
          },

          Groups: {
            flow: 'y',
            gap: 'Z1',
            childrenAs: 'state',
            childExtends: {
              flow: 'y',
              gap: 'V',
              GroupHeading: {
                tag: 'h4',
                fontSize: 'Z2',
                fontWeight: '700',
                color: 'caption',
                margin: 'Z 0 V 0',
                text: (el, s) => s.group
              },
              Rows: {
                flow: 'y',
                gap: 'V',
                childExtends: _permRow,
                childrenAs: 'state',
                children: (el, s) => PERMISSIONS_BY_GROUP[s.group].map((key) => ({ key }))
              }
            },
            children: () => PERMISSION_GROUPS.map((group) => ({ group }))
          }
        },

        // Members
        MembersCard: {
          flow: 'y',
          gap: 'Z1',
          padding: 'A',
          round: 'Z2',
          background: 'overlay',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'line',

          // Kick off lazy member fetch when the card renders. The
          // teamMembersLoaded guard in loadTeamMembers makes this
          // idempotent — repeated renders won't re-fetch.
          onInit: async (el, s) => {
            const team = _findSelectedTeam(s.root)
            if (team) await el.call('loadTeamMembers', s.root, team)
          },

          Header: {
            flow: 'y',
            gap: 'Z1',

            TopRow: {
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              gap: 'Z',
              align: 'center',
              Title: {
                tag: 'h3',
                fontSize: 'A1',
                fontWeight: '700',
                color: 'title',
                margin: '0',
                text: 'Members'
              }
            },

            InviteForm: {
              tag: 'form',
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              gap: 'Z',
              onSubmit: async (e, el, s) => {
                e.preventDefault()
                const team = _findSelectedTeam(s.root)
                const email = (s.root?._teamInviteEmail || '').trim()
                if (!team || !email) return
                s.root.update({ _teamInviteStatus: { tone: 'pending', message: `Inviting ${email}…` } })
                try {
                  await el.call('inviteToTeam', s.root, {
                    orgId: team.orgId,
                    teamId: team.id,
                    email
                  })
                  s.root.update({
                    _teamInviteEmail: '',
                    _teamInviteStatus: { tone: 'success', message: `Invitation sent to ${email}.` }
                  })
                } catch (err) {
                  console.warn('[admin/teams] inviteToTeam failed', err)
                  s.root.update({ _teamInviteStatus: { tone: 'error', message: `Invite failed: ${err?.message || err}` } })
                }
              },

              EmailInput: {
                tag: 'input',
                type: 'email',
                name: 'email',
                required: true,
                placeholder: 'email@domain.com',
                padding: 'V Z1',
                round: 'Y',
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: 'line',
                background: 'transparent',
                color: 'title',
                fontSize: 'Z2',
                value: (el, s) => s.root?._teamInviteEmail || '',
                onInput: (e, el, s) => {
                  s.root.update({ _teamInviteEmail: e.target.value })
                }
              },

              SubmitBtn: {
                tag: 'button',
                type: 'submit',
                text: 'Send invite',
                padding: 'V Z1',
                round: 'Y',
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: 'line',
                background: 'brand',
                color: 'onBrand',
                cursor: 'pointer',
                fontSize: 'Z2'
              }
            },

            Status: {
              tag: 'p',
              margin: '0',
              fontSize: 'Y1',
              hide: (el, s) => !s.root?._teamInviteStatus?.message,
              color: (el, s) => {
                const tone = s.root?._teamInviteStatus?.tone
                if (tone === 'error') return 'red'
                if (tone === 'success') return 'green'
                return 'caption'
              },
              text: (el, s) => s.root?._teamInviteStatus?.message || ''
            }
          },

          // Loading indicator — visible while member fetch is in flight
          // (team selected but not yet in teamMembersLoaded).
          LoadingHint: {
            tag: 'span',
            fontSize: 'Z2',
            color: 'caption',
            padding: 'Z1 0',
            hide: (el, s) => {
              const team = _findSelectedTeam(s.root)
              // Trigger lazy fetch whenever team changes and isn't loaded.
              // Running el.call here is a reactive side-effect — fires each
              // time this factory re-evaluates with a new unloaded team.
              if (team && !s.root?.teamMembersLoaded?.[team.id]) {
                el.call('loadTeamMembers', s.root, team)
              }
              return !team || !!s.root?.teamMembersLoaded?.[team.id]
            },
            text: 'Loading members…'
          },

          List: {
            flow: 'y',
            childExtends: _memberRow,
            childrenAs: 'state',
            children: (el, s) => {
              const team = _findSelectedTeam(s.root)
              if (!team || !s.root?.teamMembersLoaded?.[team.id]) return []
              return _teamMembers(team.id, s.root?.teamsByEmail)
            }
          },
          Empty: {
            tag: 'span',
            hide: (el, s) => {
              const team = _findSelectedTeam(s.root)
              if (!team || !s.root?.teamMembersLoaded?.[team.id]) return true
              return _teamMembers(team.id, s.root?.teamsByEmail).length > 0
            },
            padding: 'Z1 0',
            fontSize: 'Z2',
            color: 'caption',
            text: 'No members yet. Click + Invite to add someone.'
          }
        }
      }
    }
  }
}
